import { getClients } from "@agrigate/rpc-clients";
import { Provider } from "@agrigate/shared-web-ui/lib/components/ui/provider";
import { Toaster } from "@agrigate/shared-web-ui/lib/components/ui/toaster";
import { usePromiseToast } from "@agrigate/shared-web-ui/lib/hooks/use-promise-toast";
import { Button } from "@chakra-ui/react";
import { type FormEvent, type ReactElement, useMemo, useState } from "react";

export default function App(): ReactElement {
    return (
        <Provider>
            <TesterForm />
            <Toaster />
        </Provider>
    );
}

function TesterForm() {
    const [didSendOTP, setSentOTP] = useState(false);
    const [variableName, setVariableName] = useState("rpc");
    const [apiBase, setApiBase] = useState(
        "",
        // "https://svc.staging.agrigateinternal.net",
    );
    const [apiGwBase, setApiGwBase] = useState(
        location.origin === "https://apit.agrigateinternal.net"
            ? "https://apigw.theruraltrust.com"
            : "https://apigw.staging.agrigateinternal.net",
    );
    const [email, setEmail] = useState("");
    const [otp, setOtp] = useState("");
    const [token, setToken] = useState("");
    const [canInject, setCanInject] = useState(false);

    const rpc = useMemo(() => {
        const getHeaders = (): Record<string, string> => {
            if (!token) return {};
            return {
                Authorization: `Bearer ${token}`,
            };
        };
        return getClients(
            {
                useGateway: USE_GATEWAY !== "false",
                backend: {
                    url: apiBase,
                    getHeaders,
                },
                gateway: {
                    url: apiGwBase,
                    getHeaders,
                },
            },
            {
                onError: (meta, error) => {
                    console.error("API Error", meta, error);
                },
            },
        );
    }, [token, apiBase, apiGwBase]);

    const { withToast } = usePromiseToast();

    const doInject = () => {
        // biome-ignore lint/suspicious/noExplicitAny: Can not be type safe
        (window as any)[variableName] = rpc;
        console.log(`Injected ${variableName} (token: ${token})`);
    };

    const doRequestOTP = (e: FormEvent) =>
        withToast(
            async () => {
                e.preventDefault();
                const contact = email.includes("@")
                    ? { email }
                    : { phoneNumber: email };
                await rpc.auth.requestOTP({
                    ...contact,
                });
                setSentOTP(true);
            },
            {
                swallowError: true,
                messages: true,
            },
        );

    const doValidateOTP = (e: FormEvent) =>
        withToast(
            async () => {
                e.preventDefault();
                const contact = email.includes("@")
                    ? { email }
                    : { phoneNumber: email };
                if (!otp) {
                    alert("Enter OTP");
                    return;
                }
                const res = await rpc.auth.validateOTP({
                    ...contact,
                    otp,
                });
                if (res.token) {
                    setToken(res.token);
                    setCanInject(true);
                } else {
                    console.error("Response has no token");
                }
            },
            {
                swallowError: true,
                messages: true,
            },
        );

    return (
        <div style={{ padding: "20px" }}>
            <div style={{ marginBottom: "16px" }}>
                <label
                    htmlFor="apiBase"
                    style={{ display: "block", marginBottom: "8px" }}
                >
                    API Base:
                </label>
                <input
                    id="apiBase"
                    type="text"
                    value={apiBase}
                    onChange={e => setApiBase(e.target.value)}
                    style={{
                        width: "100%",
                        padding: "8px",
                        boxSizing: "border-box",
                        borderBottom: "1px solid",
                    }}
                />
            </div>

            <div style={{ marginBottom: "16px" }}>
                <label
                    htmlFor="apiGwBase"
                    style={{ display: "block", marginBottom: "8px" }}
                >
                    API GW Base:
                </label>
                <input
                    id="apiGwBase"
                    type="text"
                    value={apiGwBase}
                    onChange={e => setApiGwBase(e.target.value)}
                    style={{
                        width: "100%",
                        padding: "8px",
                        boxSizing: "border-box",
                        borderBottom: "1px solid",
                    }}
                />
            </div>

            <div style={{ marginBottom: "16px" }}>
                <label
                    htmlFor="variableName"
                    style={{ display: "block", marginBottom: "8px" }}
                >
                    Variable Name:
                </label>
                <input
                    id="variableName"
                    type="text"
                    value={variableName}
                    onChange={e => setVariableName(e.target.value)}
                    style={{
                        width: "100%",
                        padding: "8px",
                        boxSizing: "border-box",
                        borderBottom: "1px solid",
                    }}
                />
            </div>

            <div style={{ marginBottom: "16px" }}>
                <label
                    htmlFor="email"
                    style={{ display: "block", marginBottom: "8px" }}
                >
                    Email/Phone:
                </label>
                <input
                    id="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    style={{
                        width: "100%",
                        padding: "8px",
                        boxSizing: "border-box",
                        borderBottom: "1px solid",
                    }}
                />
            </div>

            <div style={{ marginBottom: "16px" }}>
                <label
                    htmlFor="otp"
                    style={{ display: "block", marginBottom: "8px" }}
                >
                    OTP:
                </label>
                <input
                    id="otp"
                    type="text"
                    value={otp}
                    onChange={e => setOtp(e.target.value)}
                    style={{
                        width: "100%",
                        padding: "8px",
                        boxSizing: "border-box",
                        borderBottom: "1px solid",
                    }}
                />
            </div>

            <div style={{ display: "flex", gap: 5 }}>
                <Button onClick={doRequestOTP}>Request OTP</Button>
                {didSendOTP ? (
                    <Button onClick={doValidateOTP}>Validate OTP</Button>
                ) : null}
                {canInject ? (
                    <Button onClick={doInject}>Inject Variable</Button>
                ) : null}
            </div>
        </div>
    );
}
